/** @format */

import { useRef, useState, useCallback, memo, useMemo } from "react";
import {
  UploadCloud,
  X,
  File as FileIcon,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
} from "lucide-react";
import { cn } from "../../utils/cn";
import { Input } from "./input";
import {
  useMultipleVideoThumbnails,
  ThumbnailState,
} from "../../utils/videoThumbnailGenerator";

export interface FlowUploaderProps {
  flowType: string;
  fileTypes: string[];
  acceptedMimeTypes: string[];
  maxFiles?: number;
  onFilesChange: (files: File[]) => void;
  files: File[];
  className?: string;
  // Video-specific props
  additionalText?: string;
  onAdditionalTextChange?: (text: string) => void;
  // Reorder props for video steps
  onMoveLeft?: (index: number) => void;
  onMoveRight?: (index: number) => void;
  fileNotes?: Record<string, string>;
  onFileNoteChange?: (fileName: string, note: string) => void;
  // assembly
  onAssemblyChange?: (file: File | null) => void;
  assemblyFile?: File | null;
  /** Rendered between upload/assembly zone and file grid — ideal for action bars */
  actionSlot?: React.ReactNode;
  maxUploadBytes?: number | null;
}

const FileCard = memo(
  ({
    file,
    idx,
    isVideoFlow,
    onRemoveFile,
    onMoveLeft,
    onMoveRight,
    filesLength,
    fileNote,
    onFileNoteChange,
    thumbnailState,
  }: {
    file: File;
    idx: number;
    isVideoFlow: boolean;
    onRemoveFile: (index: number) => void;
    onMoveLeft?: (index: number) => void;
    onMoveRight?: (index: number) => void;
    filesLength: number;
    fileNote: string;
    onFileNoteChange: (fileName: string, note: string) => void;
    thumbnailState?: ThumbnailState;
  }) => {
    // Local state for UI only
    const [isNotesExpanded, setIsNotesExpanded] = useState(false);

    const hasNotes = isVideoFlow && fileNote.trim().length > 0;

    const handleNoteChange = (newNote: string) => {
      onFileNoteChange(file.name, newNote);
    };

    const toggleNotes = () => {
      setIsNotesExpanded(!isNotesExpanded);
    };

    return (
      <div className="flex flex-col group">
        {/* File Card */}
        <div
          data-file-index={idx}
          data-file-name={file.name}
          className="group relative flex flex-col gap-2.5 items-start bg-gradient-to-br from-gray-900/85 to-black/90 border border-gray-800/20 rounded-xl p-3 hover:border-blue-500/40 hover:shadow-md hover:shadow-blue-500/10 hover:-translate-y-0.5 transition-all duration-300 backdrop-blur-sm"
        >
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              {isVideoFlow && (
                <div className="w-6 h-6 bg-gradient-to-br from-blue-500/30 to-cyan-500/30 border border-blue-400/50 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-bold text-blue-300">
                    {idx + 1}
                  </span>
                </div>
              )}
              <span className="text-sm font-semibold text-white truncate min-w-0">
                {file.name}
              </span>
              {isVideoFlow && hasNotes && (
                <div className="w-2 h-2 bg-blue-400 rounded-full flex-shrink-0"></div>
              )}
            </div>
            <button
              className="w-6 h-6 rounded-full bg-red-500/20 border border-red-500/30 flex items-center justify-center flex-shrink-0 hover:bg-red-500/30 hover:border-red-500/50 transition-all duration-200"
              onClick={() => onRemoveFile(idx)}
            >
              <X className="w-3 h-3 text-red-400" />
            </button>
          </div>

          <div className="flex items-center gap-2 text-xs text-slate-400">
            <span>{(file.size / 1024 / 1024).toFixed(1)}MB</span>
            {file.type.startsWith("video") && (
              <>
                <span>•</span>
                <span
                  className={cn(
                    "transition-colors duration-200",
                    thumbnailState?.isLoading
                      ? "text-blue-400"
                      : thumbnailState?.error
                        ? "text-red-400"
                        : "text-slate-400",
                  )}
                >
                  {thumbnailState?.duration ||
                    (thumbnailState?.isLoading
                      ? "Generating..."
                      : thumbnailState?.error
                        ? "Error"
                        : "Processing...")}
                </span>
              </>
            )}
          </div>

          {file.type.startsWith("image") ? (
            <div className="relative w-full overflow-hidden rounded-lg">
              <img
                src={URL.createObjectURL(file)}
                alt={file.name}
                className="w-full h-32 object-cover"
                draggable={false}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
            </div>
          ) : file.type.startsWith("video") ? (
            <div className="relative w-full">
              {thumbnailState?.thumbnail ? (
                <div className="relative overflow-hidden rounded-lg">
                  <img
                    src={thumbnailState.thumbnail}
                    alt={`Thumbnail for ${file.name}`}
                    className="w-full h-32 object-cover"
                    draggable={false}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
                  <div className="absolute bottom-2 right-2 bg-black/80 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-md border border-white/20">
                    {thumbnailState.duration}
                  </div>
                </div>
              ) : thumbnailState?.error ? (
                <div className="w-full h-32 bg-gradient-to-br from-red-900/30 to-red-800/40 rounded-lg flex items-center justify-center border border-red-600/25">
                  <div className="flex flex-col items-center gap-2 text-red-400">
                    <X className="w-6 h-6" />
                    <span className="text-xs text-center">
                      Failed to generate thumbnail
                    </span>
                  </div>
                </div>
              ) : (
                <div className="w-full h-32 bg-gradient-to-br from-slate-700/50 to-slate-800/60 rounded-lg flex items-center justify-center border border-slate-600/25">
                  <div className="flex flex-col items-center gap-2 text-slate-400">
                    <div
                      className={cn(
                        "w-6 h-6 border-2 border-blue-400 border-t-transparent rounded-full animate-spin",
                        thumbnailState?.isLoading && "border-blue-300",
                      )}
                    ></div>
                    <span
                      className={cn(
                        "text-xs transition-colors duration-200",
                        thumbnailState?.isLoading
                          ? "text-blue-300"
                          : "text-slate-400",
                      )}
                    >
                      {thumbnailState?.isLoading
                        ? "Generating thumbnail..."
                        : "Processing video..."}
                    </span>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="w-full h-32 bg-gradient-to-br from-slate-700/50 to-slate-800/60 rounded-lg flex items-center justify-center border border-slate-600/25">
              <div className="flex flex-col items-center gap-2 text-slate-400">
                <FileIcon className="w-8 h-8" />
                <span className="text-xs font-medium">
                  .{file.name.split(".").pop()?.toUpperCase()}
                </span>
              </div>
            </div>
          )}

          {/* Inline Notes for Video Flow */}
          {isVideoFlow && (
            <div className="w-full space-y-2">
              <button
                onClick={toggleNotes}
                className="flex items-center gap-1 text-xs text-slate-400 hover:text-blue-400 transition-colors px-2 py-1 rounded-md hover:bg-blue-500/10"
              >
                {isNotesExpanded ? (
                  <ChevronUp className="w-3 h-3" />
                ) : (
                  <ChevronDown className="w-3 h-3" />
                )}
                {hasNotes ? "Edit Context" : "Add Context"}
              </button>

              {isNotesExpanded && (
                <div className="space-y-2">
                  <Input
                    type="text"
                    placeholder={`Enter notes for ${file.name} (e.g., parts, tools, procedures shown in this video)...`}
                    value={fileNote}
                    onChange={(e) => {
                      handleNoteChange(e.target.value);
                    }}
                    className="w-full text-xs bg-slate-800/50 border border-slate-600/30 text-white placeholder:text-slate-400 focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/20"
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Reorder buttons outside the card for video flow */}
        {isVideoFlow && (onMoveLeft || onMoveRight) && (
          <div className="flex items-center justify-center gap-2 mt-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            <button
              onClick={() => onMoveLeft?.(idx)}
              disabled={idx === 0}
              className={cn(
                "flex items-center gap-1 text-xs px-3 py-1.5 rounded-md transition-all duration-200",
                idx === 0
                  ? "text-slate-500 cursor-not-allowed bg-slate-700/30"
                  : "text-slate-400 hover:text-blue-400 hover:bg-blue-500/10 border border-slate-600/30 hover:border-blue-500/30",
              )}
            >
              <ChevronLeft className="w-3 h-3" />
              Left
            </button>
            <button
              onClick={() => onMoveRight?.(idx)}
              disabled={idx === filesLength - 1}
              className={cn(
                "flex items-center gap-1 text-xs px-3 py-1.5 rounded-md transition-all duration-200",
                idx === filesLength - 1
                  ? "text-slate-500 cursor-not-allowed bg-slate-700/30"
                  : "text-slate-400 hover:text-blue-400 hover:bg-blue-500/10 border border-slate-600/30 hover:border-blue-500/30",
              )}
            >
              Right
              <ChevronRight className="w-3 h-3" />
            </button>
          </div>
        )}
      </div>
    );
  },
);

FileCard.displayName = "FileCard";

const FlowUploader = memo(function FlowUploader({
  flowType,
  fileTypes,
  acceptedMimeTypes,
  maxFiles = 10,
  onFilesChange,
  files,
  className,
  // additionalText = "",
  // onAdditionalTextChange,
  onMoveLeft,
  onMoveRight,
  fileNotes,
  onFileNoteChange,
  assemblyFile,
  onAssemblyChange,
  actionSlot,
  maxUploadBytes,
}: FlowUploaderProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const assemblyInputRef = useRef<HTMLInputElement>(null);

  const isVideoFlow = flowType === "video";

  // Memoize the video files to prevent unnecessary re-renders
  const videoFiles = useMemo(() => {
    return isVideoFlow
      ? files.filter((file) => file.type.startsWith("video"))
      : [];
  }, [isVideoFlow, files]);

  // Use the new multiple video thumbnails hook
  const { thumbnails: videoThumbnails } = useMultipleVideoThumbnails(
    videoFiles,
    {
      width: 300,
      batchSize: 2,
    },
  );

  const handleFiles = useCallback(
    (fileList: FileList) => {
      if (fileList.length) {
        // Check if we're already at max files
        if (files.length >= maxFiles) {
          alert(
            `Maximum ${maxFiles} files already uploaded for ${flowType} analysis. Remove some files to add new ones.`,
          );
          if (inputRef.current) inputRef.current.value = "";
          return;
        }

        // Check if adding these files would exceed the limit
        const totalFilesAfterUpload = files.length + fileList.length;
        if (totalFilesAfterUpload > maxFiles) {
          const remainingSlots = maxFiles - files.length;
          alert(
            `Cannot upload ${fileList.length} files. You can only upload ${remainingSlots} more file${remainingSlots === 1 ? "" : "s"} (${files.length}/${maxFiles} currently uploaded).`,
          );
          if (inputRef.current) inputRef.current.value = "";
          return;
        }

        const validFiles = Array.from(fileList).filter((file) => {
          if (maxUploadBytes && file.size > maxUploadBytes) {
            const maxMb = (maxUploadBytes / 1024 / 1024).toFixed(1);
            alert(
              `Your demo plan allows files up to ${maxMb} MB. Contact support for a higher limit.`,
            );
            return false;
          }
          // Check if file type is accepted
          const isValidType = acceptedMimeTypes.some(
            (type) =>
              file.type.startsWith(type) ||
              file.name.toLowerCase().endsWith(type),
          );

          // Additional check for file extensions
          const hasValidExtension = fileTypes.some((ext) =>
            file.name.toLowerCase().endsWith(ext.toLowerCase()),
          );

          if (!isValidType && !hasValidExtension) {
            alert(
              `File "${file.name}" is not a supported ${flowType} file type. Supported types: ${fileTypes.join(", ")}`,
            );
            return false;
          }

          return true;
        });

        // Final check after filtering valid files
        const finalTotalFiles = files.length + validFiles.length;
        if (finalTotalFiles > maxFiles) {
          const allowedFiles = validFiles.slice(0, maxFiles - files.length);
          alert(
            `Only ${allowedFiles.length} of ${validFiles.length} files were uploaded to stay within the ${maxFiles} file limit.`,
          );
          onFilesChange([...files, ...allowedFiles]);
        } else {
          onFilesChange([...files, ...validFiles]);
        }
      }

      // Reset input value to allow re-uploading the same file
      if (inputRef.current) inputRef.current.value = "";
    },
    [acceptedMimeTypes, fileTypes, flowType, files, maxFiles, maxUploadBytes, onFilesChange],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      if (e.dataTransfer.files) handleFiles(e.dataTransfer.files);
    },
    [handleFiles],
  );

  const removeFile = useCallback(
    (index: number) => {
      const newFiles = files.filter((_, i) => i !== index);
      onFilesChange(newFiles);
      // Reset input value to allow re-uploading the same file
      if (inputRef.current) inputRef.current.value = "";
    },
    [files, onFilesChange],
  );

  // Helper function to get thumbnail state for a specific file
  const getThumbnailStateForFile = useCallback(
    (file: File): ThumbnailState | undefined => {
      return videoThumbnails.find((thumb) => thumb.file === file);
    },
    [videoThumbnails],
  );

  const isAtMaxFiles = files.length >= maxFiles;

  return (
    <div className={cn("space-y-4", className)}>
      {/* Upload Area */}
      <div
        className={cn(
          "p-[2px] rounded-xl text-center transition-all duration-300 backdrop-blur-sm",
          isAtMaxFiles
            ? "bg-gradient-to-br from-gray-900/95 to-black/95 cursor-not-allowed opacity-60"
            : "bg-gradient-to-br from-gray-900/95 to-black/95 hover:bg-gradient-to-r hover:from-[#3C6AC6] hover:via-[#D216CC] hover:to-[#FB6B96] hover:shadow-lg hover:shadow-blue-500/20 cursor-pointer hover:-translate-y-0.5",
        )}
        onClick={() => !isAtMaxFiles && inputRef.current?.click()}
        onDragOver={!isAtMaxFiles ? handleDragOver : undefined}
        onDrop={!isAtMaxFiles ? handleDrop : undefined}
      >
        <div className="w-full h-full bg-gradient-to-br from-gray-900/95 to-black/95 rounded-xl px-6 py-8 border-2 border-dashed border-gray-600/40 hover:border-dashed hover:border-transparent">
          <div className="flex items-center gap-4">
            <div
              className={cn(
                "w-11 h-11 shrink-0 flex items-center justify-center transition-all duration-300 rounded-xl",
                isAtMaxFiles
                  ? "bg-slate-800/50 text-slate-500"
                  : "bg-gradient-to-r from-[#3C6AC6] via-[#D216CC] to-[#FB6B96]",
              )}
            >
              <UploadCloud
                className={cn(
                  "w-5 h-5 transition-all duration-300",
                  isAtMaxFiles ? "text-slate-500" : "text-white",
                )}
              />
            </div>
            <div className="text-left min-w-0">
              <p className="font-semibold text-white text-sm">
                {isAtMaxFiles
                  ? `Maximum ${maxFiles} files reached`
                  : `Upload ${flowType} files`}
              </p>
              <p className="text-xs text-slate-400 mt-0.5">
                {isAtMaxFiles
                  ? "Remove files to upload new ones"
                  : `Drag & drop or click to browse • ${fileTypes.join(", ")} • ${files.length}/${maxFiles} files`}
              </p>
            </div>
          </div>
        </div>
      </div>

      <input
        type="file"
        accept={[...acceptedMimeTypes, ...fileTypes].join(",")}
        className="hidden"
        ref={inputRef}
        multiple
        disabled={isAtMaxFiles}
        onChange={(e) =>
          e.target.files && !isAtMaxFiles && handleFiles(e.target.files)
        }
      />

      {/* Assembly */}
      {onAssemblyChange && (
        <div className="rounded-xl border border-slate-700/30 bg-slate-900/20 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <label className="text-sm font-semibold text-slate-300">
              Assembly PDF
            </label>
            {assemblyFile && (
              <button
                onClick={() => {
                  onAssemblyChange(null);
                  if (assemblyInputRef.current) {
                    assemblyInputRef.current.value = "";
                  }
                }}
                className="text-xs text-red-400 hover:text-red-300 px-2 py-1 rounded-md hover:bg-red-500/10 transition-colors duration-200"
              >
                Remove
              </button>
            )}
          </div>
          {assemblyFile ? (
            <div className="flex items-center gap-2 text-xs text-green-400 bg-green-500/10 border border-green-500/20 rounded-lg px-3 py-2">
              <svg
                className="w-3.5 h-3.5 shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span className="truncate">
                {assemblyFile.name} ({(assemblyFile.size / 1024).toFixed(1)} KB)
              </span>
            </div>
          ) : (
            <>
              <input
                ref={assemblyInputRef}
                type="file"
                accept=".pdf"
                required={["cad", "drawings"].includes(flowType)}
                onChange={(e) => {
                  const file = e.target.files?.[0] || null;
                  if (file && !file.name.toLowerCase().endsWith(".pdf")) {
                    alert("Please select a PDF file for the assembly");
                    e.target.value = "";
                    return;
                  }
                  onAssemblyChange(file);
                }}
                className="w-full text-sm text-slate-400 file:mr-4 file:py-2 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-medium file:bg-blue-500/20 file:text-blue-400 hover:file:bg-blue-500/30 file:cursor-pointer bg-slate-800/50 border border-slate-600/30 rounded-lg p-2 focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/20"
              />
              <p className="text-xs text-slate-500">
                PDF with Part #, Part Description, Qty columns
              </p>
            </>
          )}
        </div>
      )}

      {actionSlot}

      {/* File Preview */}
      {files.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <h4 className="font-semibold text-sm text-slate-300">
              Uploaded Files
            </h4>
            <span className="text-xs text-slate-500 bg-slate-800/50 px-2 py-0.5 rounded-full">
              {files.length}/{maxFiles}
            </span>
            <div className="flex-1 h-px bg-slate-800/50" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {files.map((file, idx) => (
              <FileCard
                key={`${file.name}-${idx}`}
                file={file}
                idx={idx}
                isVideoFlow={isVideoFlow}
                onRemoveFile={removeFile}
                onMoveLeft={onMoveLeft}
                onMoveRight={onMoveRight}
                filesLength={files.length}
                fileNote={fileNotes?.[file.name] || ""}
                onFileNoteChange={onFileNoteChange || (() => { })}
                thumbnailState={getThumbnailStateForFile(file)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
});

FlowUploader.displayName = "FlowUploader";

export default FlowUploader;
